import React, { useState, useEffect } from 'react';
import { Select, Button, Modal, Form, Input, message, Spin } from 'antd';
import axios from 'axios';
import { getEndpoint } from '../utils/endpoints';

const { Option } = Select;

interface Guideline {
  guideline_name: string;
  description: string;
}

const GuidelineManagement: React.FC = () => {
  const [guidelines, setGuidelines] = useState<Guideline[]>([]);
  const [selectedGuideline, setSelectedGuideline] = useState<string | null>(null);
  const [guidelineContent, setGuidelineContent] = useState<string | null>(null);
  const [directions, setDirections] = useState<string[]>([]);
  const [hasChanges, setHasChanges] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isAddingGuideline, setIsAddingGuideline] = useState(false);
  const [form] = Form.useForm();

  useEffect(() => {
    fetchGuidelines();
  }, []);

  const fetchGuidelines = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${getEndpoint('GLOSS')}/getGuidelines/`);
      setGuidelines(response.data);
    } catch (error) {
      console.error('Error fetching guidelines:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleGuidelineSelect = async (guidelineName: string) => {
    setLoading(true);
    try {
      setSelectedGuideline(guidelineName);
      const response = await axios.post(`${getEndpoint('GLOSS')}/getGuideline/`, {
        guideline_name: guidelineName,
      });
      setGuidelineContent(response.data.guidelines ? response.data.guidelines : " ");
      setDirections(response.data.direction || []);
      setHasChanges(false);
    } catch (error) {
      console.error('Error fetching guideline content:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddOrEditGuideline = async (values: Guideline) => {
    setLoading(true);
    try {
      const endpoint = isAddingGuideline
        ? `${getEndpoint('GLOSS')}/addGuideline/`
        : `${getEndpoint('GLOSS')}/editGuideline/`;
      await axios.post(endpoint, values);
      message.success(isAddingGuideline ? 'Guideline added successfully!' : 'Guideline updated successfully!');
      fetchGuidelines();
      setIsModalVisible(false);
    } catch (error) {
      console.error('Error saving guideline:', error);
      message.error('Failed to save guideline.');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteGuideline = async () => {
    if (!selectedGuideline) return;
    setLoading(true);
    try {
      await axios.post(`${getEndpoint('GLOSS')}/deleteGuideline/`, {
        guideline_name: selectedGuideline,
      });
      message.success('Guideline deleted successfully!');
      setSelectedGuideline(null);
      setGuidelineContent(null);
      setDirections([]);
      fetchGuidelines();
    } catch (error) {
      console.error('Error deleting guideline:', error);
      message.error('Failed to delete guideline.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <Spin spinning={loading}>
        <div style={{ marginBottom: 16 }}>
          <Select
            showSearch
            placeholder="Select a guideline"
            style={{ width: 300 }}
            value={selectedGuideline}
            onChange={handleGuidelineSelect}
            filterOption={(input, option) =>
              typeof option?.children === 'string' ? (option.children as string).toLowerCase().includes(input.toLowerCase()) : false
            }
          >
            {guidelines.map((guideline) => (
              <Option key={guideline.guideline_name} value={guideline.guideline_name}>
                {guideline.guideline_name}
              </Option>
            ))}
          </Select>
          <Button
                                  style={{ marginLeft: '10px' }}

            onClick={() => {
              setIsAddingGuideline(true);
              form.resetFields();
              setIsModalVisible(true);
            }}
          >
            Add Guideline
          </Button>
          {selectedGuideline && (
            <Button onClick={handleDeleteGuideline} danger                         style={{ marginLeft: '10px' }}
>
              Delete Guideline
            </Button>
          )}
        </div>
        {guidelineContent && (
          <div style={{ marginBottom: 16 }}>
            <Form.Item >
              <Input.TextArea
                value={guidelineContent}
                rows={8}
                onChange={(e) => {
                  setGuidelineContent(e.target.value);
                  setHasChanges(true);
                }}
              />
            </Form.Item>
            <Form.Item label="Allowed directions">
              <Select
                mode="tags"
                placeholder="Select or add directions"
                value={directions}
                onChange={(value) => {
                  setDirections(value);
                  setHasChanges(true);
                }}
              >
                <Option value="en-it">en-it</Option>
                <Option value="it-en">it-en</Option>
                <Option value="en-de">en-de</Option>
                <Option value="de-en">de-en</Option>
              </Select>
            </Form.Item>
          </div>
        )}
                    {hasChanges && (
                        <Button
                        type="primary"
                        style={{ marginLeft: '10px' }}
                        onClick={async () => {
                          if (!selectedGuideline || !guidelineContent) return;
                          setLoading(true);
                          try {
                            await axios.post(`${getEndpoint('GLOSS')}/saveGuideline/`, {
                              guideline_name: selectedGuideline,
                              guidelines: guidelineContent,
                              direction: directions,
                            });
                            message.success('Guideline saved successfully!');
                            setHasChanges(false);
                          } catch (error) {
                            console.error('Error saving guideline:', error);
                            message.error('Failed to save guideline.');
                          } finally {
                            setLoading(false);
                          }
                        }}
                        disabled={!hasChanges}
                      >
                        Save
                      </Button>
                    )}
      </Spin>

      <Modal
        title={isAddingGuideline ? 'Add Guideline' : 'Edit Guideline'}
        open={isModalVisible}
        onCancel={() => setIsModalVisible(false)}
        onOk={() => form.submit()}
      >
        <Form form={form} onFinish={handleAddOrEditGuideline} layout="vertical">
          <Form.Item label="Guideline Name" name="guideline_name" rules={[{ required: true }]}>            
            <Input disabled={!isAddingGuideline} />
          </Form.Item>
          <Form.Item label="Description" name="description" rules={[{ required: true }]}>            
            <Input.TextArea />
          </Form.Item>
          <Form.Item label="Allowed Direction" name="direction" rules={[{ required: true }]}>            
            <Select mode="tags" placeholder="Select or add directions">
              <Option value="en-it">en-it</Option>
              <Option value="it-en">it-en</Option>
              <Option value="en-de">en-de</Option>
              <Option value="de-en">de-en</Option>
            </Select>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default GuidelineManagement;
